import { useEffect, useRef } from "react";
import * as THREE from "three";
import type { AnimalTrack, AnimalTrackPoint, GlobeObservation } from "../types/telemetry";

type EarthSceneProps = {
  observations?: GlobeObservation[];
  animalTracks?: AnimalTrack[];
};

type EarthSceneState = {
  animalLayer: THREE.Group;
  earthGroup: THREE.Group;
  phenomenonLayer: THREE.Group;
  typhoonMarker?: THREE.Object3D;
};

export function EarthScene({ observations = [], animalTracks = [] }: EarthSceneProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const labelRefs = useRef<Array<HTMLDivElement | null>>([]);
  const animalLabelRefs = useRef<Array<HTMLDivElement | null>>([]);
  const observationsRef = useRef(observations);
  const animalTracksRef = useRef(animalTracks);
  const sceneStateRef = useRef<EarthSceneState | null>(null);

  useEffect(() => {
    observationsRef.current = observations;
    animalTracksRef.current = animalTracks;

    const sceneState = sceneStateRef.current;
    if (!sceneState) return;

    const nextPhenomenonLayer = createPhenomenonLayer(observations);
    sceneState.earthGroup.remove(sceneState.phenomenonLayer);
    disposeObject(sceneState.phenomenonLayer);
    sceneState.earthGroup.add(nextPhenomenonLayer);
    sceneState.phenomenonLayer = nextPhenomenonLayer;
    sceneState.typhoonMarker = nextPhenomenonLayer.getObjectByName("typhoon-marker");

    const nextAnimalLayer = createAnimalLayer(animalTracks);
    sceneState.earthGroup.remove(sceneState.animalLayer);
    disposeObject(sceneState.animalLayer);
    sceneState.earthGroup.add(nextAnimalLayer);
    sceneState.animalLayer = nextAnimalLayer;
  }, [animalTracks, observations]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    let cameraDistance = 7.8;
    let targetCameraDistance = cameraDistance;
    camera.position.set(0, 0.28, cameraDistance);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const textureLoader = new THREE.TextureLoader();
    const earthMap = textureLoader.load("/textures/earth_atmos_2048.jpg", (texture) => {
      const balancedMap = createBalancedEarthTexture(texture.image as HTMLImageElement);
      balancedMap.anisotropy = 8;
      earth.material.map = balancedMap;
      earth.material.needsUpdate = true;
      texture.dispose();
      earth.userData.balancedMap = balancedMap;
    });
    earthMap.colorSpace = THREE.SRGBColorSpace;
    earthMap.anisotropy = 8;

    const specularMap = textureLoader.load("/textures/earth_specular_2048.jpg");
    specularMap.anisotropy = 8;

    const cloudMap = textureLoader.load("/textures/earth_clouds_1024.png");
    cloudMap.colorSpace = THREE.SRGBColorSpace;
    cloudMap.anisotropy = 8;

    const earthGroup = new THREE.Group();
    scene.add(earthGroup);

    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(2.075, 96, 96),
      new THREE.MeshBasicMaterial({
        color: 0x8defff,
        transparent: true,
        opacity: 0.12,
        blending: THREE.AdditiveBlending,
        side: THREE.BackSide,
      }),
    );
    earthGroup.add(atmosphere);

    const earth = new THREE.Mesh(
      new THREE.SphereGeometry(2, 128, 128),
      new THREE.MeshPhongMaterial({
        map: earthMap,
        color: new THREE.Color(0xd5dfcc),
        specularMap,
        specular: new THREE.Color(0x183b4d),
        shininess: 16,
        emissive: new THREE.Color(0x071a25),
        emissiveIntensity: 0.1,
      }),
    );
    earthGroup.add(earth);

    const cloudLayer = new THREE.Mesh(
      new THREE.SphereGeometry(2.035, 128, 128),
      new THREE.MeshLambertMaterial({
        map: cloudMap,
        transparent: true,
        opacity: 0.54,
        depthWrite: false,
      }),
    );
    earthGroup.add(cloudLayer);

    const nightShadow = createNightShadowLayer();
    earthGroup.add(nightShadow);

    const phenomenonLayer = createPhenomenonLayer(observationsRef.current);
    earthGroup.add(phenomenonLayer);
    const typhoonMarker = phenomenonLayer.getObjectByName("typhoon-marker");

    const animalLayer = createAnimalLayer(animalTracksRef.current);
    earthGroup.add(animalLayer);
    sceneStateRef.current = { animalLayer, earthGroup, phenomenonLayer, typhoonMarker };

    const aurora = createAuroraRing();
    earthGroup.add(aurora);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(2.82, 0.006, 8, 160),
      new THREE.MeshBasicMaterial({
        color: 0x66f5ff,
        transparent: true,
        opacity: 0.42,
      }),
    );
    const ringBaseRotation = new THREE.Euler(1.22, 0.2, -0.24);
    ring.rotation.copy(ringBaseRotation);
    scene.add(ring);

    const route = new THREE.Mesh(
      new THREE.TorusGeometry(2.52, 0.004, 8, 160),
      new THREE.MeshBasicMaterial({
        color: 0xffd166,
        transparent: true,
        opacity: 0.58,
      }),
    );
    const routeBaseRotation = new THREE.Euler(0.78, -0.58, 0.56);
    route.rotation.copy(routeBaseRotation);
    scene.add(route);

    const stars = createStars();
    scene.add(stars);

    const currentSkyDate = new Date();
    const celestialLayer = createCelestialLayer(currentSkyDate);
    scene.add(celestialLayer);

    const ambient = new THREE.AmbientLight(0xe8fcff, 0.64);
    scene.add(ambient);

    const hemisphere = new THREE.HemisphereLight(0xf6fdff, 0x24577a, 0.72);
    scene.add(hemisphere);

    const sunPosition = getSunVector(currentSkyDate, 9);
    const sun = new THREE.DirectionalLight(0xfff2d8, 5.15);
    sun.position.copy(sunPosition);
    scene.add(sun);

    const rim = new THREE.DirectionalLight(0xa6f5ff, 1.85);
    rim.position.set(-4, -0.8, -2);
    scene.add(rim);

    const resize = () => {
      const { width, height } = mount.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);

    let pinchStartDistance = 0;
    let pinchStartCameraDistance = cameraDistance;
    let isTouchDragging = false;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let isDragging = false;
    let dragPointerId = -1;
    let lastPointerX = 0;
    let lastPointerY = 0;
    let manualRotationX = 0;
    let manualRotationY = 0;
    const maxManualTilt = THREE.MathUtils.degToRad(74);
    const activePointers = new Set<number>();
    const minCameraDistance = 4.35;
    const maxCameraDistance = 10.2;
    const setTargetCameraDistance = (distance: number) => {
      targetCameraDistance = THREE.MathUtils.clamp(distance, minCameraDistance, maxCameraDistance);
    };
    const getTouchDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        event.preventDefault();
        isTouchDragging = true;
        pinchStartDistance = 0;
        lastTouchX = event.touches[0].clientX;
        lastTouchY = event.touches[0].clientY;
        return;
      }

      if (event.touches.length === 2) {
        event.preventDefault();
        isTouchDragging = false;
        pinchStartDistance = getTouchDistance(event.touches);
        pinchStartCameraDistance = targetCameraDistance;
      }
    };
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 1 && isTouchDragging) {
        event.preventDefault();
        const touch = event.touches[0];
        const dx = touch.clientX - lastTouchX;
        const dy = touch.clientY - lastTouchY;
        lastTouchX = touch.clientX;
        lastTouchY = touch.clientY;
        manualRotationY += dx * 0.0075;
        manualRotationX = THREE.MathUtils.clamp(manualRotationX + dy * 0.005, -maxManualTilt, maxManualTilt);
        return;
      }

      if (event.touches.length === 2 && pinchStartDistance > 0) {
        event.preventDefault();
        const currentDistance = getTouchDistance(event.touches);
        const zoomScale = currentDistance / pinchStartDistance;
        setTargetCameraDistance(pinchStartCameraDistance / zoomScale);
      }
    };
    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length < 2) {
        pinchStartDistance = 0;
      }
      if (event.touches.length === 0) {
        isTouchDragging = false;
      } else if (event.touches.length === 1) {
        isTouchDragging = true;
        lastTouchX = event.touches[0].clientX;
        lastTouchY = event.touches[0].clientY;
      }
    };
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      setTargetCameraDistance(targetCameraDistance + event.deltaY * 0.004);
    };
    const handlePointerDown = (event: PointerEvent) => {
      activePointers.add(event.pointerId);
      if (activePointers.size !== 1) {
        isDragging = false;
        return;
      }
      isDragging = true;
      dragPointerId = event.pointerId;
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;
      renderer.domElement.setPointerCapture(event.pointerId);
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (!isDragging || event.pointerId !== dragPointerId || activePointers.size !== 1) return;
      const dx = event.clientX - lastPointerX;
      const dy = event.clientY - lastPointerY;
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;
      manualRotationY += dx * 0.006;
      manualRotationX = THREE.MathUtils.clamp(manualRotationX + dy * 0.004, -maxManualTilt, maxManualTilt);
    };
    const handlePointerUp = (event: PointerEvent) => {
      activePointers.delete(event.pointerId);
      if (event.pointerId === dragPointerId) {
        isDragging = false;
        dragPointerId = -1;
      }
      if (renderer.domElement.hasPointerCapture(event.pointerId)) {
        renderer.domElement.releasePointerCapture(event.pointerId);
      }
    };
    const updateObservationLabels = (elapsed: number) => {
      const { width, height } = mount.getBoundingClientRect();
      const cameraPosition = camera.position.clone();
      const occupiedRects: LabelRect[] = [];
      earthGroup.updateMatrixWorld(true);
      camera.updateMatrixWorld();

      const currentObservations = observationsRef.current;
      const candidates = currentObservations.flatMap((observation, index) => {
        const element = labelRefs.current[index];
        if (!element) return [];
        element.style.opacity = "0";
        element.classList.remove("is-compact");

        if (observation.attachment === "sky") {
          const x = ((observation.x ?? 84) / 100) * width;
          const y = ((observation.y ?? 17) / 100) * height;
          return [{ element, observation, x, y, priority: getObservationPriority(observation) }];
        }

        const lat =
          observation.attachment === "iss"
            ? 7 + Math.sin(elapsed * 0.55) * 18
            : observation.lat;
        const lon =
          observation.attachment === "iss"
            ? normalizeLon(75 + elapsed * 18)
            : observation.lon;

        if (lat == null || lon == null) {
          return [];
        }

        const worldPosition = latLonToVector3(lat, lon, 2.22).applyMatrix4(earthGroup.matrixWorld);
        const surfaceNormal = worldPosition.clone().normalize();
        const viewDirection = cameraPosition.clone().sub(worldPosition).normalize();
        const facesCamera = surfaceNormal.dot(viewDirection) > 0.06;
        const projected = worldPosition.clone().project(camera);
        const isInFrame =
          projected.x > -1.16 && projected.x < 1.16 && projected.y > -1.16 && projected.y < 1.16;

        if (!facesCamera || !isInFrame) {
          return [];
        }

        const x = (projected.x * 0.5 + 0.5) * width + 18;
        const y = (-projected.y * 0.5 + 0.5) * height - 8;
        return [{ element, observation, x, y, priority: getObservationPriority(observation) }];
      });

      candidates
        .sort((a, b) => b.priority - a.priority)
        .forEach((candidate) => {
          const rect = getLabelRect(candidate.element, candidate.x, candidate.y);
          const collides = occupiedRects.some((occupied) => rectsOverlap(rect, occupied, -6));
          const labelIndex = occupiedRects.length;
          const staggerY = collides ? (labelIndex % 3) * 14 - 14 : 0;
          const staggerX = collides ? 12 + (labelIndex % 2) * 8 : 0;
          const x = candidate.x + staggerX;
          const y = candidate.y + staggerY;

          candidate.element.classList.remove("is-compact");
          candidate.element.style.opacity = "1";
          candidate.element.style.transform = `translate3d(${x}px, ${y}px, 0)`;
          occupiedRects.push(getLabelRect(candidate.element, x, y));
      });

      return occupiedRects;
    };
    const updateAnimalTracks = (elapsed: number, occupiedRects: LabelRect[]) => {
      const { width, height } = mount.getBoundingClientRect();
      const cameraPosition = camera.position.clone();
      earthGroup.updateMatrixWorld(true);
      camera.updateMatrixWorld();

      const activeAnimalLayer = sceneStateRef.current?.animalLayer;
      if (!activeAnimalLayer) return;

      animalTracksRef.current.forEach((track, index) => {
        const animalGroup = activeAnimalLayer.getObjectByName(`animal-${track.id}`);
        const marker = animalGroup?.getObjectByName("animal-marker");
        const labelSprite = animalGroup?.getObjectByName("animal-label");
        const label = animalLabelRefs.current[index];
        const position = getAnimalPosition(track, elapsed);

        if (marker) {
          marker.position.copy(latLonToVector3(position.lat, position.lon, 2.18));
        }
        if (labelSprite) {
          labelSprite.position.copy(latLonToVector3(position.lat, position.lon, 2.42));
        }

        if (!label) return;
        label.style.opacity = "0";
        label.classList.remove("is-compact");

        const worldPosition = latLonToVector3(position.lat, position.lon, 2.3).applyMatrix4(earthGroup.matrixWorld);
        const surfaceNormal = worldPosition.clone().normalize();
        const viewDirection = cameraPosition.clone().sub(worldPosition).normalize();
        const facesCamera = surfaceNormal.dot(viewDirection) > 0.04;
        const projected = worldPosition.clone().project(camera);
        const isInFrame =
          projected.x > -1.16 && projected.x < 1.16 && projected.y > -1.16 && projected.y < 1.16;

        if (!facesCamera || !isInFrame) {
          return;
        }

        const x = (projected.x * 0.5 + 0.5) * width + 18;
        const y = (-projected.y * 0.5 + 0.5) * height - 8;
        const rect = getLabelRect(label, x, y);
        const collides = occupiedRects.some((occupied) => rectsOverlap(rect, occupied, -6));
        const staggerY = collides ? (occupiedRects.length % 3) * 14 - 14 : 0;
        const staggerX = collides ? 12 + (occupiedRects.length % 2) * 8 : 0;
        label.classList.remove("is-compact");
        label.style.opacity = "1";
        label.style.transform = `translate3d(${x + staggerX}px, ${y + staggerY}px, 0)`;
        occupiedRects.push(getLabelRect(label, x + staggerX, y + staggerY));
      });
    };

    let frameId = 0;
    const clock = new THREE.Clock();
    const baseRotation = getRotationForLongitude(139.7);
    const japanLatitudeTilt = THREE.MathUtils.degToRad(35.7);
    let ringSpin = ringBaseRotation.z;
    let routeSpin = routeBaseRotation.z;
    let starDrift = 0;
    earthGroup.rotation.x = japanLatitudeTilt;
    const animate = () => {
      const delta = clock.getDelta();
      const elapsed = clock.elapsedTime;
      const viewRotationX = manualRotationX * 0.78;
      const viewRotationY = manualRotationY * 0.78;
      cameraDistance = THREE.MathUtils.lerp(cameraDistance, targetCameraDistance, 1 - Math.pow(0.001, delta));
      camera.position.z = cameraDistance;
      earthGroup.rotation.x = japanLatitudeTilt + manualRotationX;
      earthGroup.rotation.y = baseRotation + elapsed * 0.026 + manualRotationY;
      cloudLayer.rotation.y += delta * 0.03;
      updateNightShadow(nightShadow, sun.position, earthGroup);
      const activeSceneState = sceneStateRef.current;
      activeSceneState?.phenomenonLayer.children.forEach((child, index) => {
        child.scale.setScalar(1 + Math.sin(elapsed * 1.55 + index) * 0.012);
      });
      if (activeSceneState?.typhoonMarker) {
        activeSceneState.typhoonMarker.rotation.z -= delta * 1.8;
      }
      aurora.rotation.z += delta * 0.05;
      ringSpin += delta * 0.035;
      routeSpin -= delta * 0.026;
      starDrift += delta * 0.0015;
      ring.rotation.set(
        ringBaseRotation.x + viewRotationX * 0.7,
        ringBaseRotation.y + viewRotationY * 0.72,
        ringSpin,
      );
      route.rotation.set(
        routeBaseRotation.x + viewRotationX * 0.58,
        routeBaseRotation.y + viewRotationY * 0.64,
        routeSpin,
      );
      stars.rotation.x = viewRotationX * 0.28;
      stars.rotation.y = starDrift + viewRotationY * 0.34;
      celestialLayer.rotation.x = viewRotationX;
      celestialLayer.rotation.y = viewRotationY;
      sun.position.copy(sunPosition.clone().applyEuler(celestialLayer.rotation));
      const occupiedRects = updateObservationLabels(elapsed);
      updateAnimalTracks(elapsed, occupiedRects);
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };

    resize();
    animate();
    window.addEventListener("resize", resize);
    renderer.domElement.addEventListener("touchstart", handleTouchStart, { passive: false });
    renderer.domElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    renderer.domElement.addEventListener("touchend", handleTouchEnd);
    renderer.domElement.addEventListener("touchcancel", handleTouchEnd);
    renderer.domElement.addEventListener("wheel", handleWheel, { passive: false });
    renderer.domElement.addEventListener("pointerdown", handlePointerDown);
    renderer.domElement.addEventListener("pointermove", handlePointerMove);
    renderer.domElement.addEventListener("pointerup", handlePointerUp);
    renderer.domElement.addEventListener("pointercancel", handlePointerUp);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener("touchstart", handleTouchStart);
      renderer.domElement.removeEventListener("touchmove", handleTouchMove);
      renderer.domElement.removeEventListener("touchend", handleTouchEnd);
      renderer.domElement.removeEventListener("touchcancel", handleTouchEnd);
      renderer.domElement.removeEventListener("wheel", handleWheel);
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("pointerup", handlePointerUp);
      renderer.domElement.removeEventListener("pointercancel", handlePointerUp);
      mount.removeChild(renderer.domElement);
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          const material = object.material;
          if (Array.isArray(material)) {
            material.forEach((item) => item.dispose());
          } else {
            material.dispose();
          }
        } else if (object instanceof THREE.Sprite) {
          object.material.map?.dispose();
          object.material.dispose();
        } else if (object instanceof THREE.Points) {
          object.geometry.dispose();
          object.material.dispose();
        }
      });
      renderer.dispose();
      earthMap.dispose();
      if (earth.userData.balancedMap instanceof THREE.Texture) {
        earth.userData.balancedMap.dispose();
      }
      specularMap.dispose();
      cloudMap.dispose();
      sceneStateRef.current = null;
    };
  }, []);

  return (
    <>
      <div className="earth-canvas" ref={mountRef} />
      <div className="globe-observations" aria-label="地球上の見どころ">
        {observations.map((item, index) => (
          <div
            className={`globe-note globe-note-${item.tone}`}
            key={`${item.title}-${item.note}`}
            ref={(element) => {
              labelRefs.current[index] = element;
            }}
          >
            <span>{item.title}</span>
            <small>{item.note}</small>
          </div>
        ))}
      </div>
      <div className="animal-observations" aria-label="生き物の移動">
        {animalTracks.map((item, index) => (
          <div
            className="animal-note"
            key={item.id}
            ref={(element) => {
              animalLabelRefs.current[index] = element;
            }}
          >
            <span>{item.name}</span>
            <small>{item.note}</small>
          </div>
        ))}
      </div>
    </>
  );
}

type LabelRect = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

function getLabelRect(element: HTMLElement, x: number, y: number, compact = false): LabelRect {
  const width = compact ? Math.min(element.offsetWidth || 112, 112) : element.offsetWidth || 132;
  const height = compact ? 18 : element.offsetHeight || 34;
  return {
    bottom: y + height,
    left: x,
    right: x + width,
    top: y,
  };
}

function rectsOverlap(a: LabelRect, b: LabelRect, padding: number) {
  return !(
    a.right + padding < b.left ||
    a.left > b.right + padding ||
    a.bottom + padding < b.top ||
    a.top > b.bottom + padding
  );
}

function disposeObject(object: THREE.Object3D) {
  object.traverse((child) => {
    if (child instanceof THREE.Mesh || child instanceof THREE.Line || child instanceof THREE.Points) {
      child.geometry.dispose();
      const material = child.material;
      if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
      } else {
        material.map?.dispose();
        material.dispose();
      }
    } else if (child instanceof THREE.Sprite) {
      child.material.map?.dispose();
      child.material.dispose();
    }
  });
}

function getObservationPriority(observation: GlobeObservation) {
  if (observation.attachment === "iss") return 100;
  if (observation.title === "台風の芽") return 92;
  if (observation.title === "地震") return 88;
  if (observation.title === "火災") return 84;
  if (observation.title === "火山") return 82;
  if (observation.title === "オーロラ") return 78;
  if (observation.title === "雨雲") return 74;
  if (observation.title === "海流") return 70;
  if (observation.title === "海の熱") return 68;
  return 64;
}

function createPhenomenonLayer(observations: GlobeObservation[]) {
  const group = new THREE.Group();
  let hasIssMarker = false;
  let hasTyphoonMarker = false;

  observations.forEach((observation) => {
    if (observation.attachment === "sky") return;

    const color = getObservationColor(observation);
    const lat = observation.attachment === "iss" ? observation.lat ?? 9 : observation.lat;
    const lon = observation.attachment === "iss" ? observation.lon ?? 75 : observation.lon;

    if (lat == null || lon == null) return;

    const marker =
      observation.attachment === "iss"
        ? createIssMarker(lat, lon, observation.note, observation.dataStatus === "live")
        : createObservationMarker(observation, lat, lon, color);

    if (observation.attachment === "iss" && !hasIssMarker) {
      marker.name = "iss-marker";
      marker.userData.isLivePosition = observation.dataStatus === "live";
      hasIssMarker = true;
    }

    group.add(marker);

    if (observation.title === "台風の芽" && !hasTyphoonMarker) {
      const typhoonMarker = createTyphoonMarker(lat, lon);
      typhoonMarker.name = "typhoon-marker";
      group.add(typhoonMarker);
      hasTyphoonMarker = true;
    }
  });

  return group;
}

function getObservationColor(observation: GlobeObservation) {
  if (observation.title === "火災") return 0xff8a3d;
  if (observation.title === "火山") return 0xffc462;
  if (observation.attachment === "iss") return 0xffd166;

  switch (observation.tone) {
    case "amber":
      return 0xffd166;
    case "rose":
      return 0xff8aa0;
    case "green":
      return 0x8dffb2;
    case "cyan":
      return 0x7ee7ff;
    default:
      return 0xf8fdff;
  }
}

function getObservationMarkerSize(observation: GlobeObservation) {
  if (observation.attachment === "iss") return 0.06;
  if (observation.title === "火災" || observation.title === "火山") return 0.052;
  if (observation.title === "地震") return 0.048;
  return 0.05;
}

function createObservationMarker(observation: GlobeObservation, lat: number, lon: number, color: number) {
  const isLive = observation.dataStatus === "live";

  if (observation.title === "火災") {
    return createTerrainEventMarker(lat, lon, color, observation.title, observation.note, isLive, "fire");
  }

  if (observation.title === "火山") {
    return createTerrainEventMarker(lat, lon, color, observation.title, observation.note, isLive, "volcano");
  }

  if (observation.title === "地震") {
    return createTerrainEventMarker(lat, lon, color, observation.title, observation.note, isLive, "quake");
  }

  return createMarker(
    lat,
    lon,
    color,
    getObservationMarkerSize(observation),
    observation.title,
    observation.note,
    isLive,
  );
}

function createAnimalLayer(tracks: AnimalTrack[]) {
  const group = new THREE.Group();

  tracks.forEach((track) => {
    const animal = new THREE.Group();
    animal.name = `animal-${track.id}`;

    const routePoints = track.path.map((point) => latLonToVector3(point.lat, point.lon, 2.135));
    const routeGeometry = new THREE.BufferGeometry().setFromPoints(routePoints);
    const route = new THREE.Line(
      routeGeometry,
      new THREE.LineBasicMaterial({
        color: track.color,
        transparent: true,
        opacity: 0.42,
        blending: THREE.AdditiveBlending,
      }),
    );
    animal.add(route);

    const marker = new THREE.Mesh(
      new THREE.SphereGeometry(0.038, 18, 12),
      new THREE.MeshBasicMaterial({
        color: track.color,
        transparent: true,
        opacity: 0.96,
      }),
    );
    marker.name = "animal-marker";
    marker.add(createGlow(0.13, track.color));
    animal.add(marker);

    const label = createSurfaceLabelSprite(track.name, track.note, false);
    label.name = "animal-label";
    label.position.copy(latLonToVector3(track.path[0]?.lat ?? 0, track.path[0]?.lon ?? 0, 2.42));
    animal.add(label);

    group.add(animal);
  });

  return group;
}

function getAnimalPosition(track: AnimalTrack, elapsed: number) {
  if (track.path.length === 0) return { lat: 0, lon: 0 };
  if (track.path.length === 1) return track.path[0];

  const segmentCount = track.path.length - 1;
  const progress = (elapsed * track.speed) % 1;
  const scaledProgress = progress * segmentCount;
  const startIndex = Math.floor(scaledProgress);
  const localProgress = scaledProgress - startIndex;
  const start = track.path[startIndex];
  const end = track.path[Math.min(startIndex + 1, track.path.length - 1)];

  return interpolateTrackPoint(start, end, smoothStep(localProgress));
}

function interpolateTrackPoint(start: AnimalTrackPoint, end: AnimalTrackPoint, progress: number) {
  return {
    lat: THREE.MathUtils.lerp(start.lat, end.lat, progress),
    lon: interpolateLongitude(start.lon, end.lon, progress),
  };
}

function interpolateLongitude(start: number, end: number, progress: number) {
  const delta = normalizeLon(end - start);
  return normalizeLon(start + delta * progress);
}

function smoothStep(value: number) {
  return value * value * (3 - 2 * value);
}

function createBalancedEarthTexture(image: HTMLImageElement) {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  canvas.width = Number(width);
  canvas.height = Number(height);
  const context = canvas.getContext("2d", { willReadFrequently: true });

  if (!context) {
    const fallback = new THREE.CanvasTexture(canvas);
    fallback.colorSpace = THREE.SRGBColorSpace;
    return fallback;
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const frame = context.getImageData(0, 0, canvas.width, canvas.height);
  const data = frame.data;

  for (let index = 0; index < data.length; index += 4) {
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];
    const brightness = red * 0.3 + green * 0.59 + blue * 0.11;
    const isOcean = blue > red * 1.08 && blue > green * 1.03;
    const isBrightLand = brightness > 150 && red > blue * 1.02 && green > blue * 0.86;

    if (isBrightLand) {
      data[index] = clampColor(red * 0.7 + 42);
      data[index + 1] = clampColor(green * 0.68 + 38);
      data[index + 2] = clampColor(blue * 0.56 + 24);
    } else if (isOcean) {
      data[index] = clampColor(red * 0.86);
      data[index + 1] = clampColor(green * 0.94);
      data[index + 2] = clampColor(blue * 1.04 + 8);
    } else {
      data[index] = clampColor(red * 0.9 + 12);
      data[index + 1] = clampColor(green * 0.92 + 10);
      data[index + 2] = clampColor(blue * 0.9 + 8);
    }
  }

  context.putImageData(frame, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function clampColor(value: number) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function createMarker(
  lat: number,
  lon: number,
  color: number,
  size: number,
  label?: string,
  note?: string,
  isLive = false,
) {
  const group = new THREE.Group();
  group.userData = { lat, lon };
  const position = latLonToVector3(lat, lon, 2.09);
  const marker = new THREE.Mesh(
    new THREE.SphereGeometry(size, 24, 16),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.94,
    }),
  );
  marker.position.copy(position);
  marker.add(createGlow(size * 2.5, color));
  group.add(marker);

  if (label) {
    const labelSprite = createSurfaceLabelSprite(label, note ?? "", isLive);
    labelSprite.position.copy(latLonToVector3(lat, lon, 2.42));
    group.add(labelSprite);
  }

  return group;
}

function createTerrainEventMarker(
  lat: number,
  lon: number,
  color: number,
  label: string,
  note: string | undefined,
  isLive: boolean,
  kind: "fire" | "quake" | "volcano",
) {
  const group = new THREE.Group();
  group.userData = { lat, lon };
  const radius = 2.111;
  const size = kind === "fire" ? 0.46 : kind === "quake" ? 0.72 : 0.54;
  const opacity = kind === "fire" ? 0.52 : kind === "quake" ? 0.38 : 0.46;

  if (kind === "quake") {
    group.add(createTerrainPatch(lat, lon, radius, size, color, opacity, "ring"));
    group.add(createTerrainPatch(lat, lon, radius + 0.004, size * 0.48, 0xffffff, 0.42, "ring"));
  } else if (kind === "fire") {
    group.add(createTerrainPatch(lat, lon, radius, size, color, opacity, "fireHalo"));
    group.add(createTerrainPatch(lat + 0.1, lon - 0.12, radius + 0.004, size * 0.34, 0xfff2b2, 0.72, "fireCore"));
    group.add(createTerrainPatch(lat - 0.08, lon + 0.11, radius + 0.003, size * 0.24, 0xff3d2e, 0.36, "fireCore"));
  } else {
    group.add(createTerrainPatch(lat, lon, radius, size, color, opacity, "soft"));
    group.add(createTerrainPatch(lat + 0.18, lon - 0.24, radius + 0.003, size * 0.38, 0xfff2b2, 0.56, "hot"));
  }

  const labelSprite = createSurfaceLabelSprite(label, note ?? "", isLive);
  labelSprite.position.copy(latLonToVector3(lat, lon, 2.45));
  group.add(labelSprite);

  return group;
}

function createTerrainPatch(
  lat: number,
  lon: number,
  radius: number,
  size: number,
  color: number,
  opacity: number,
  style: "fireCore" | "fireHalo" | "hot" | "ring" | "soft",
) {
  const texture = createTerrainPatchTexture(color, style);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const patch = new THREE.Mesh(new THREE.PlaneGeometry(size, size), material);
  const position = latLonToVector3(lat, lon, radius);
  const normal = position.clone().normalize();
  patch.position.copy(position);
  patch.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
  patch.renderOrder = 12;
  return patch;
}

function createTerrainPatchTexture(color: number, style: "fireCore" | "fireHalo" | "hot" | "ring" | "soft") {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  const accent = new THREE.Color(color);
  const center = "rgba(255, 246, 205, 0.95)";
  const mid = `rgba(${Math.round(accent.r * 255)}, ${Math.round(accent.g * 255)}, ${Math.round(accent.b * 255)}, 0.62)`;
  const edge = `rgba(${Math.round(accent.r * 255)}, ${Math.round(accent.g * 255)}, ${Math.round(accent.b * 255)}, 0)`;

  if (context) {
    context.clearRect(0, 0, canvas.width, canvas.height);

    if (style === "ring") {
      context.strokeStyle = "rgba(255, 255, 255, 0.9)";
      context.lineWidth = 13;
      context.beginPath();
      context.arc(128, 128, 76, 0, Math.PI * 2);
      context.stroke();
      context.strokeStyle = mid;
      context.lineWidth = 28;
      context.beginPath();
      context.arc(128, 128, 82, 0, Math.PI * 2);
      context.stroke();
    } else {
      const gradientRadius = style === "fireCore" ? 76 : style === "fireHalo" ? 92 : 122;
      const gradient = context.createRadialGradient(128, 128, 3, 128, 128, gradientRadius);
      gradient.addColorStop(0, style === "hot" || style === "fireCore" ? center : mid);
      gradient.addColorStop(style === "fireCore" ? 0.12 : style === "fireHalo" ? 0.18 : style === "hot" ? 0.22 : 0.34, mid);
      if (style === "fireHalo") {
        gradient.addColorStop(0.52, `rgba(${Math.round(accent.r * 255)}, ${Math.round(accent.g * 255)}, ${Math.round(accent.b * 255)}, 0.16)`);
      }
      gradient.addColorStop(1, edge);
      context.fillStyle = gradient;
      context.fillRect(0, 0, canvas.width, canvas.height);

      if (style === "hot" || style === "fireCore") {
        context.fillStyle = "rgba(255, 255, 230, 0.92)";
        context.beginPath();
        context.arc(126, 130, style === "fireCore" ? 22 : 18, 0, Math.PI * 2);
        context.fill();
      }
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function createIssMarker(lat: number, lon: number, note = "", isLive = false) {
  const group = new THREE.Group();
  group.userData = { lat, lon };
  const position = latLonToVector3(lat, lon, 2.24);
  const normal = position.clone().normalize();
  const tangent = new THREE.Vector3(-normal.z, 0, normal.x).normalize();
  const bitangent = normal.clone().cross(tangent).normalize();
  const material = new THREE.MeshBasicMaterial({
    color: 0xffd166,
    transparent: true,
    opacity: 0.96,
  });
  const core = new THREE.Mesh(new THREE.BoxGeometry(0.074, 0.048, 0.032), material);
  const leftPanel = new THREE.Mesh(new THREE.BoxGeometry(0.105, 0.036, 0.01), material);
  const rightPanel = new THREE.Mesh(new THREE.BoxGeometry(0.105, 0.036, 0.01), material);
  const trail = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      position.clone().add(tangent.clone().multiplyScalar(-0.32)).add(bitangent.clone().multiplyScalar(-0.04)),
      position.clone().add(tangent.clone().multiplyScalar(-0.14)).add(bitangent.clone().multiplyScalar(-0.01)),
      position.clone(),
    ]),
    new THREE.LineBasicMaterial({
      color: 0xffd166,
      transparent: true,
      opacity: 0.52,
      blending: THREE.AdditiveBlending,
    }),
  );
  core.position.copy(position);
  leftPanel.position.copy(position.clone().add(tangent.clone().multiplyScalar(-0.09)));
  rightPanel.position.copy(position.clone().add(tangent.clone().multiplyScalar(0.09)));
  core.lookAt(0, 0, 0);
  leftPanel.lookAt(0, 0, 0);
  rightPanel.lookAt(0, 0, 0);

  const glow = createGlow(0.18, 0xffd166);
  glow.position.copy(position);

  const label = createSurfaceLabelSprite("ISS", note, isLive);
  label.position.copy(latLonToVector3(lat, lon, 2.55));

  group.add(trail, glow, leftPanel, core, rightPanel, label);
  return group;
}

function createNightShadowLayer() {
  return new THREE.Mesh(
    new THREE.SphereGeometry(2.045, 128, 128),
    new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        sunDirection: { value: new THREE.Vector3(1, 0, 0) },
        nightColor: { value: new THREE.Color(0x021126) },
      },
      vertexShader: `
        varying vec3 vNormal;

        void main() {
          vNormal = normalize(normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 sunDirection;
        uniform vec3 nightColor;
        varying vec3 vNormal;

        void main() {
          float lightAmount = dot(normalize(vNormal), normalize(sunDirection));
          float nightAmount = 1.0 - smoothstep(-0.18, 0.24, lightAmount);
          float edgeGlow = smoothstep(-0.08, 0.16, lightAmount) * (1.0 - smoothstep(0.16, 0.42, lightAmount));
          float alpha = nightAmount * 0.46 + edgeGlow * 0.08;
          gl_FragColor = vec4(nightColor, alpha);
        }
      `,
    }),
  );
}

function updateNightShadow(shadow: THREE.Mesh, sunWorldPosition: THREE.Vector3, earthGroup: THREE.Group) {
  const material = shadow.material;
  if (!(material instanceof THREE.ShaderMaterial)) return;

  const sunDirection = sunWorldPosition.clone().normalize();
  const inverseEarthRotation = earthGroup.quaternion.clone().invert();
  sunDirection.applyQuaternion(inverseEarthRotation);
  material.uniforms.sunDirection.value.copy(sunDirection);
}

function createGlow(size: number, color: number) {
  return new THREE.Mesh(
    new THREE.SphereGeometry(size, 24, 16),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.16,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
}

function createSurfaceLabelSprite(title: string, note: string, isLive = false) {
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 180;
  const context = canvas.getContext("2d");

  if (context) {
    context.clearRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = isLive ? "#ffffff" : "rgba(178, 190, 198, 0.9)";
    context.font = "900 46px sans-serif";
    context.textAlign = "left";
    context.textBaseline = "top";
    context.shadowColor = "transparent";
    context.shadowBlur = 0;
    context.fillText(title, 42, 38, 556);

    if (note) {
      context.fillStyle = isLive ? "rgba(245, 252, 255, 0.94)" : "rgba(158, 173, 184, 0.84)";
      context.font = "760 31px sans-serif";
      context.fillText(note, 42, 94, 556);
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;

  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      opacity: isLive ? 1 : 0.78,
      depthTest: true,
      depthWrite: false,
    }),
  );
  sprite.scale.set(1.14, 0.32, 1);
  sprite.renderOrder = 20;
  return sprite;
}

function createSurfaceRing(lat: number, lon: number, color: number) {
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.19, 0.006, 8, 72),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.86,
      blending: THREE.AdditiveBlending,
    }),
  );
  const position = latLonToVector3(lat, lon, 2.105);
  ring.position.copy(position);
  ring.lookAt(0, 0, 0);
  ring.rotateZ(0.7);
  return ring;
}

function createTyphoonMarker(lat: number, lon: number) {
  const group = new THREE.Group();
  const ring = createSurfaceRing(lat, lon, 0x9df7ff);
  const inner = createSurfaceRing(lat + 0.4, lon - 0.4, 0xeffcff);
  inner.scale.setScalar(0.62);
  const arm = createSurfacePatch(lat + 1.8, lon + 3, 2.112, 0xffffff, 0.26);
  arm.scale.set(0.58, 0.11, 0.012);
  arm.rotateZ(0.5);
  group.add(ring, inner, arm);
  return group;
}

function createAuroraRing() {
  const aurora = new THREE.Mesh(
    new THREE.TorusGeometry(1.12, 0.035, 12, 160),
    new THREE.MeshBasicMaterial({
      color: 0x69f0a4,
      transparent: true,
      opacity: 0.36,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  aurora.position.y = 1.56;
  aurora.rotation.x = Math.PI / 2;
  aurora.scale.set(1.08, 0.84, 1);
  return aurora;
}

function createSurfacePatch(lat: number, lon: number, radius: number, color: number, opacity: number) {
  const patch = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 32, 16),
    new THREE.MeshStandardMaterial({
      color,
      transparent: true,
      opacity,
      roughness: 1,
    }),
  );
  patch.position.copy(latLonToVector3(lat, lon, radius));
  patch.lookAt(0, 0, 0);
  return patch;
}

function createStars() {
  const geometry = new THREE.BufferGeometry();
  const count = 1500;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);

  for (let i = 0; i < count; i += 1) {
    const radius = 18 + seededNoise(i * 7.3) * 22;
    const theta = seededNoise(i * 13.7) * Math.PI * 2;
    const phi = Math.acos(2 * seededNoise(i * 19.1) - 1);
    const warmth = seededNoise(i * 29.9);
    positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = radius * Math.cos(phi);
    colors[i * 3] = 0.78 + warmth * 0.22;
    colors[i * 3 + 1] = 0.86 + warmth * 0.12;
    colors[i * 3 + 2] = 1;
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  return new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      size: 0.026,
      transparent: true,
      opacity: 0.9,
      vertexColors: true,
      sizeAttenuation: true,
    }),
  );
}

function createCelestialLayer(date: Date) {
  const group = new THREE.Group();
  const fixedStars = [
    { name: "シリウス", ra: 6.752, dec: -16.716, color: 0xbfe8ff, size: 0.055 },
    { name: "ベガ", ra: 18.616, dec: 38.783, color: 0xd7efff, size: 0.045 },
    { name: "アルタイル", ra: 19.846, dec: 8.868, color: 0xe7f5ff, size: 0.038 },
    { name: "デネブ", ra: 20.691, dec: 45.28, color: 0xd3ecff, size: 0.038 },
    { name: "アンタレス", ra: 16.49, dec: -26.432, color: 0xffb17d, size: 0.046 },
    { name: "北極星", ra: 2.53, dec: 89.264, color: 0xf4fbff, size: 0.034 },
  ];

  fixedStars.forEach((star) => {
    const marker = createCelestialMarker(star.name, star.color, star.size);
    marker.position.copy(raDecToVector3(star.ra, star.dec, 22, date));
    group.add(marker);
  });

  getPlanetPositions(date).forEach((planet) => {
    const marker = createCelestialMarker(planet.name, planet.color, planet.size);
    marker.position.copy(raDecToVector3(planet.ra, planet.dec, 18.5, date));
    group.add(marker);
  });

  const sunPosition = getSunPosition(date);
  const sun = createSunMarker();
  sun.position.copy(raDecToVector3(sunPosition.ra, sunPosition.dec, 19.5, date));
  group.add(sun);

  const moonPosition = getMoonPosition(date);
  const moon = createMoonMarker();
  moon.position.copy(raDecToVector3(moonPosition.ra, moonPosition.dec, 15.5, date));
  group.add(moon);

  return group;
}

function createSunMarker() {
  const group = new THREE.Group();
  const sun = new THREE.Mesh(
    new THREE.SphereGeometry(0.14, 32, 20),
    new THREE.MeshBasicMaterial({
      color: 0xfff1a6,
      transparent: true,
      opacity: 0.98,
      blending: THREE.AdditiveBlending,
    }),
  );
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.44, 32, 20),
    new THREE.MeshBasicMaterial({
      color: 0xffd166,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  const label = createDistantLabelSprite("太陽", 0xffdf7e);
  label.position.set(0.42, 0.08, 0);
  label.scale.set(0.78, 0.2, 1);
  group.add(glow, sun, label);
  return group;
}

function createMoonMarker() {
  const group = new THREE.Group();
  const moon = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 36, 24),
    new THREE.MeshStandardMaterial({
      color: 0xd8d3c5,
      roughness: 0.92,
      metalness: 0,
      emissive: new THREE.Color(0x302f2b),
      emissiveIntensity: 0.16,
    }),
  );
  const craterMaterial = new THREE.MeshBasicMaterial({
    color: 0x8f8a7e,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
  });
  [
    { x: -0.045, y: 0.055, z: 0.17, s: 0.028 },
    { x: 0.045, y: 0.005, z: 0.175, s: 0.022 },
    { x: -0.01, y: -0.055, z: 0.178, s: 0.018 },
  ].forEach((crater) => {
    const spot = new THREE.Mesh(new THREE.CircleGeometry(crater.s, 18), craterMaterial);
    spot.position.set(crater.x, crater.y, crater.z);
    group.add(spot);
  });
  const label = createDistantLabelSprite("月", 0xf6f1e8);
  label.position.set(0.34, 0.09, 0);
  label.scale.set(0.78, 0.2, 1);
  group.add(moon, label);
  return group;
}

function createCelestialMarker(label: string, color: number, size: number) {
  const group = new THREE.Group();
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(size, 16, 12),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.96,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(size * 3.4, 16, 12),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.12,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  const labelSprite = createDistantLabelSprite(label, color);
  labelSprite.position.set(size * 10, size * 2.6, 0);
  group.add(glow, core, labelSprite);
  return group;
}

function createDistantLabelSprite(text: string, color: number) {
  const canvas = document.createElement("canvas");
  canvas.width = 300;
  canvas.height = 78;
  const context = canvas.getContext("2d");
  if (context) {
    context.fillStyle = "rgba(4, 14, 28, 0.64)";
    context.strokeStyle = `#${color.toString(16).padStart(6, "0")}`;
    context.lineWidth = 2;
    context.beginPath();
    context.roundRect(10, 14, 280, 48, 24);
    context.fill();
    context.stroke();
    context.fillStyle = "#f7fcff";
    context.font = "800 30px sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, 150, 39);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      opacity: 0.92,
      depthWrite: false,
    }),
  );
  sprite.scale.set(1.95, 0.5, 1);
  return sprite;
}

function getPlanetPositions(date: Date) {
  const days = (date.getTime() - Date.UTC(2000, 0, 1, 12)) / 86_400_000;
  const planets = [
    { name: "金星", period: 224.701, longitude: 181.98, color: 0xfff0ba, size: 0.055 },
    { name: "火星", period: 686.98, longitude: 355.43, color: 0xff8f6a, size: 0.046 },
    { name: "木星", period: 4332.59, longitude: 34.35, color: 0xffd18a, size: 0.062 },
    { name: "土星", period: 10759.22, longitude: 50.08, color: 0xf6e2a2, size: 0.052 },
  ];

  return planets.map((planet, index) => {
    const eclipticLongitude = normalizeDegrees(planet.longitude + (360 * days) / planet.period);
    const eclipticLatitude = Math.sin(THREE.MathUtils.degToRad(days * 0.08 + index * 81)) * 2.1;
    const { ra, dec } = eclipticToRaDec(eclipticLongitude, eclipticLatitude);
    return { ...planet, ra, dec };
  });
}

function getMoonPosition(date: Date) {
  const days = (date.getTime() - Date.UTC(2000, 0, 1, 12)) / 86_400_000;
  const meanLongitude = normalizeDegrees(218.316 + 13.176396 * days);
  const meanAnomaly = normalizeDegrees(134.963 + 13.064993 * days);
  const argumentLatitude = normalizeDegrees(93.272 + 13.22935 * days);
  const longitude = meanLongitude + 6.289 * Math.sin(THREE.MathUtils.degToRad(meanAnomaly));
  const latitude = 5.128 * Math.sin(THREE.MathUtils.degToRad(argumentLatitude));
  return eclipticToRaDec(normalizeDegrees(longitude), latitude);
}

function getSunPosition(date: Date) {
  const days = (date.getTime() - Date.UTC(2000, 0, 1, 12)) / 86_400_000;
  const meanLongitude = normalizeDegrees(280.46646 + 0.98564736 * days);
  const meanAnomaly = normalizeDegrees(357.52911 + 0.98560028 * days);
  const anomalyRad = THREE.MathUtils.degToRad(meanAnomaly);
  const eclipticLongitude =
    meanLongitude +
    1.914602 * Math.sin(anomalyRad) +
    0.019993 * Math.sin(2 * anomalyRad) +
    0.000289 * Math.sin(3 * anomalyRad);

  return eclipticToRaDec(normalizeDegrees(eclipticLongitude), 0);
}

function getSunVector(date: Date, radius: number) {
  const sunPosition = getSunPosition(date);
  return raDecToVector3(sunPosition.ra, sunPosition.dec, radius, date);
}

function eclipticToRaDec(longitude: number, latitude: number) {
  const lon = THREE.MathUtils.degToRad(longitude);
  const lat = THREE.MathUtils.degToRad(latitude);
  const obliquity = THREE.MathUtils.degToRad(23.43928);
  const x = Math.cos(lat) * Math.cos(lon);
  const y = Math.cos(lat) * Math.sin(lon) * Math.cos(obliquity) - Math.sin(lat) * Math.sin(obliquity);
  const z = Math.cos(lat) * Math.sin(lon) * Math.sin(obliquity) + Math.sin(lat) * Math.cos(obliquity);
  const ra = normalizeDegrees(THREE.MathUtils.radToDeg(Math.atan2(y, x))) / 15;
  const dec = THREE.MathUtils.radToDeg(Math.asin(z));
  return { ra, dec };
}

function raDecToVector3(raHours: number, decDegrees: number, radius: number, date: Date) {
  const siderealHours = getGreenwichSiderealHours(date);
  const hourAngle = THREE.MathUtils.degToRad((siderealHours - raHours) * 15);
  const dec = THREE.MathUtils.degToRad(decDegrees);

  return new THREE.Vector3(
    radius * Math.cos(dec) * Math.sin(hourAngle),
    radius * Math.sin(dec),
    -radius * Math.cos(dec) * Math.cos(hourAngle),
  );
}

function getGreenwichSiderealHours(date: Date) {
  const julianDate = date.getTime() / 86_400_000 + 2_440_587.5;
  const daysSinceJ2000 = julianDate - 2_451_545.0;
  return normalizeDegrees(280.46061837 + 360.98564736629 * daysSinceJ2000) / 15;
}

function normalizeDegrees(degrees: number) {
  return ((degrees % 360) + 360) % 360;
}

function seededNoise(seed: number) {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
}

function normalizeLon(lon: number) {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

function getRotationForLongitude(longitude: number) {
  return THREE.MathUtils.degToRad(90 - (longitude + 180));
}

function latLonToVector3(lat: number, lon: number, radius: number) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);

  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta),
  );
}
